import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { List, ListItem, ListItemButton } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import { formatPhoneNumber } from '@/app/module/conversations/format-helpers'
import { ContactType, CustomFieldType, GroupType, SubscriptionType } from '@/app/module/conversations/types'

type Props = {
  contactInfo: ContactType
}

const ContactInfo: React.FC<Props> = (props) => {
  const { contactInfo } = props
  const {
    contactId,
    firstName,
    lastName,
    countryCode,
    fullPhoneNumber,
    groups,
    segments,
    customFields,
    subscriptions,
  } = contactInfo
  return (
    <InfoWrapper>
      <InfoCard>
        <InfoTitle>
          <Typography variant="subtitle2">
            {firstName} {lastName}
          </Typography>
          <Typography variant="h6">{formatPhoneNumber(contactId, countryCode, fullPhoneNumber)}</Typography>
        </InfoTitle>
      </InfoCard>
      {customFields && <Section title="Custom Fields" data={customFields} isCustomField />}
      {groups && (
        <Section
          title="Groups"
          data={groups}
          isCustomField={false}
          url={(id: number) => `/contacts/?filterType=groups&filterId=${id}&page=1`}
        />
      )}
      {segments && (
        <Section
          title="Segments"
          data={segments}
          isCustomField={false}
          url={(id: number) => `/contacts/?filterType=segments&filterId=${id}&page=1`}
        />
      )}
      {subscriptions && (
        <Section
          title="Active Campaigns"
          data={subscriptions}
          isCustomField={false}
          url={(id: number) => `/campaigns/${id}`}
        />
      )}
    </InfoWrapper>
  )
}

type SectionProps = {
  data: GroupType[] | CustomFieldType[] | SubscriptionType[]
  title: string
  isCustomField: boolean
  url?: (id: number) => string
}

const Section: React.FC<SectionProps> = ({ data, title, isCustomField, url }) => {
  const hasData = Boolean(data && data.length > 0)

  return (
    <Accordion defaultExpanded={false} elevation={0} square>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${title}-content`} id={`${title}-header`}>
        <Typography variant="h6" color="primary">
          {title} ({hasData ? data.length : 0})
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
        }}
      >
        <List>
          {(data as Array<GroupType | CustomFieldType | SubscriptionType>).map((element: any, index) => (
            <React.Fragment key={index}>
              {isCustomField ? (
                <ListItem>
                  <Typography variant="subtitle2" color="textSecondary">
                    <strong>{element.name}:</strong> {element.value}
                  </Typography>
                </ListItem>
              ) : (
                <StyledListItem>
                  <ListItemButton
                    component="a"
                    href={url ? url(element.id) : '#'}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {element.name}
                    </Typography>
                    <IconButton size="small">
                      <Icon>chevron_right</Icon>
                    </IconButton>
                  </ListItemButton>
                </StyledListItem>
              )}
            </React.Fragment>
          ))}
          {!data.length && (
            <Typography variant="body2" color="textSecondary" px={2}>
              No {title.toLowerCase()} available.
            </Typography>
          )}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

const InfoWrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.highlight.light,
  paddingBottom: '1px',
}))

const InfoCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: '10px 20px',
  marginBottom: '5px',
}))

const InfoTitle = styled('div')(() => ({
  textAlign: 'center',
}))

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.border.main}`,
  padding: 0,
  '&:last-child': {
    borderBottom: 'none',
  },
}))

export default ContactInfo
